import { baseRequest, getRequest } from '../Util/utils';
import type { ExerciseReference } from './api';

export function simpleResponse<T>(response: Response): Promise<T> {
  if (response.ok) return response.json();
  throw `${response.status} ${response.statusText}`;
}

export type ComposerPayload = {
  body: string;
  files?: File[];
  references?: ExerciseReference[];
  replyToId?: string;
  // Extra scalar fields a specific caller needs serialized alongside body/attachments/references
  // (e.g. Comments' postDayComment needs `forUser`) -- appended identically on both the JSON and
  // multipart paths.
  extra?: Record<string, string | undefined>;
};

// Posts body text plus any staged files/exercise references as a single request. Uses the cheap
// JSON path when there's nothing to upload; switches to multipart (own boundary, so don't use
// getRequest()'s forced JSON content-type) once there's at least one file, appending files under
// 'attachments[]' and references as bracket-notation fields (references[i][type]/[path]). Shared
// by Messaging/api.ts::sendMessage and Comments/api.ts::postActivityComment/postDayComment, which
// each duplicated this exact shape before this extraction.
export async function postComposerPayload<T>(url: string, payload: ComposerPayload): Promise<T> {
  const files = payload.files ?? [];
  const references = payload.references ?? [];
  const extra = payload.extra ?? {};

  if (files.length === 0) {
    return window
      .fetch(url, {
        ...getRequest(),
        method: 'POST',
        body: JSON.stringify({ body: payload.body, references, reply_to: payload.replyToId, ...extra }),
      })
      .then((r) => simpleResponse<T>(r));
  }

  const form = new FormData();
  form.append('body', payload.body);
  files.forEach((file) => form.append('attachments[]', file));
  references.forEach((ref, i) => {
    form.append(`references[${i}][type]`, ref.type);
    form.append(`references[${i}][path]`, ref.path);
  });
  if (payload.replyToId) form.append('reply_to', payload.replyToId);
  Object.entries(extra).forEach(([key, value]) => {
    if (value != null) form.append(key, value);
  });

  return window
    .fetch(url, { ...baseRequest(), method: 'POST', body: form })
    .then((r) => simpleResponse<T>(r));
}
